import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosClient from "../axios-client";
import CourierBalanceCard from "../components/CourierBalanceCard";
import { useStateContext } from "../contexts/ContextProvider";
import { holdReasonLabel, statusMeta, fmtDateTime } from "../components/Order/orderPrimitives";

/**
 * Overview Dashboard — the company-wide admin overview imported from the Claude
 * Design project ("Overview Dashboard.dc.html"). It is the counterpart to the
 * Teams leaderboard: everything here spans every team. Wired to
 * /dashboard/overview (admin only). Theme-aware (dark + light) per the app's
 * design convention; the design's own breadcrumb/top-bar chrome is dropped
 * since the app supplies its header and sidebar.
 */

const palette = (dark) =>
  dark
    ? { bg: "#0a0d13", surface: "#11151c", surface2: "#1a1f29", border: "#232935", text: "#e8ebf1", muted: "#8a93a3", dim: "#616b7c" }
    : { bg: "#f8fafc", surface: "#ffffff", surface2: "#f1f5f9", border: "#e2e8f0", text: "#0f172a", muted: "#64748b", dim: "#94a3b8" };

const MONO = "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace";

/**
 * Reading order of the hold split. UNKNOWN is last and unlabelled as a category
 * because it is not one — it is holds placed before the reason column existed.
 */
const HOLD_BREAKDOWN = [
  ["REPAIR", "repair"],
  ["RESCHEDULE", "reschedule"],
  ["FABRIC_OUT_OF_STOCK", "fabric out of stock"],
  ["OTHER", "other"],
  ["UNKNOWN", "unspecified"],
];
const SANS = "'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif";

const RANGES = [
  { key: "month", label: "This Month" },
  { key: "today", label: "Today" },
  { key: "last30", label: "Last 30 days" },
];

const CHANNEL_MODES = [
  ["breakdown", "This range"],
  ["trend", "12-month trend"],
];

const PRODUCT_DIMS = [
  ["fabric", "Fabric type"],
  ["type", "Product type"],
  ["color", "Color"],
];
const PRODUCT_METRICS = [
  ["revenue", "৳ Revenue"],
  ["units", "Units"],
];

const SOURCE_COLORS = ["#3b82f6", "#8b5cf6", "#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#64748b"];
const TREND_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#0ea5e9", "#10b981"];
const AVATAR_PALETTE = ["#3b82f6", "#8b5cf6", "#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#6366f1"];

// Status label → chip colours + glyph, keyed by the backend's statusName rather
// than the status id, which is why this cannot simply call statusMeta(). The
// hues are the dark-theme half of the shared palette, kept in step by hand; the
// glyphs are this screen's own. Approved/Booking and Ready/QA used to share a
// hue here, which is the same collision the shared palette was rebuilt to fix.
const STATUS_STYLE = {
  Draft: { color: "#94a3b8", bg: "rgba(148,163,184,.12)", icon: "◦" },
  Approved: { color: "#a78bfa", bg: "rgba(167,139,250,.12)", icon: "✎" },
  Print: { color: "#c084fc", bg: "rgba(192,132,252,.12)", icon: "⎙" },
  "In Production": { color: "#fbbf24", bg: "rgba(251,191,36,.12)", icon: "◍" },
  "Ready to Deliver": { color: "#22d3ee", bg: "rgba(34,211,238,.12)", icon: "▲" },
  Booking: { color: "#60a5fa", bg: "rgba(96,165,250,.12)", icon: "◇" },
  "Out for Delivery": { color: "#38bdf8", bg: "rgba(56,189,248,.12)", icon: "→" },
  "On Hold": { color: "#e879f9", bg: "rgba(232,121,249,.12)", icon: "⏸" },
  Delivered: { color: "#34d399", bg: "rgba(52,211,153,.12)", icon: "✓" },
  Returned: { color: "#f87171", bg: "rgba(248,113,113,.12)", icon: "↩" },
  Cancelled: { color: "#a8a29e", bg: "rgba(168,162,158,.12)", icon: "✕" },
  "In QA": { color: "#a1a1aa", bg: "rgba(161,161,170,.12)", icon: "◔" },
};
const statusStyle = (name) => STATUS_STYLE[name] || { color: "#8a93a3", bg: "rgba(138,147,163,.12)", icon: "•" };

/** The hold hue, taken from the map above so the tile, the section and the chip
 *  are the one colour the rest of the app already uses for ON HOLD. */
const HOLD_STYLE = statusStyle("On Hold");

const fmtBDT = (n) => "BDT " + Math.round(Number(n) || 0).toLocaleString("en-US");
const fmtShort = (n) => {
  n = Math.round(Number(n) || 0);
  if (n >= 1000000) return "BDT " + (n / 1000000).toFixed(2) + "M";
  if (n >= 1000) return "BDT " + (n / 1000).toFixed(0) + "K";
  return "BDT " + n.toLocaleString("en-US");
};
const shortNum = (n) => {
  n = Math.round(Number(n) || 0);
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(0) + "K";
  return String(n);
};
const initials = (s) => (s || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const avatarBg = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const fmtDelta = (v, suffix = "%") => (v === null || v === undefined ? "" : (v > 0 ? "▲ " : v < 0 ? "▼ " : "") + Math.abs(v).toFixed(1) + suffix);
const deltaColor = (v, muted) => (v === null || v === undefined ? muted : v >= 0 ? "#4ade80" : "#fca5a5");
const timeAgo = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h ago";
  return Math.floor(h / 24) + "d ago";
};

const OverviewDashboard = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { darkMode, accentColor } = useStateContext();
  const c = palette(darkMode);

  const [range, setRange] = useState("month");
  const [channelMode, setChannelMode] = useState("breakdown");
  const [productDim, setProductDim] = useState("fabric");
  const [productMetric, setProductMetric] = useState("revenue");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = useCallback(
    (rangeKey) => {
      setLoading(true);
      axiosClient
        .get(`/dashboard/overview?range=${rangeKey}`)
        .then(({ data }) => {
          setData(data);
          setLoading(false);
        })
        .catch((error) => {
          const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
          toast.error(message);
          setLoading(false);
        });
    },
    [axiosClient]
  );

  useEffect(() => {
    fetchData(range);
  }, [range, fetchData]);

  const seg = (active) => ({ background: active ? accentColor : "transparent", color: active ? "#fff" : c.muted });
  const board = data?.range?.board || "";
  const k = data?.kpis;
  const triage = data?.triage;
  const hold = data?.hold;
  const delayedProduction = data?.delayedProduction;
  const dormant = data?.dormant;
  const breakdown = data?.channel?.breakdown;
  const trend = data?.channel?.trend;
  const performers = data?.performers || [];
  const activity = data?.activity || [];
  const stock = data?.products?.stock;
  // Non-admins get the same board scoped to their own data; a few company-only
  // sections (dormant merchants) drop out and a couple of labels change.
  const isPersonal = data?.scope === "personal";

  const totalOrders = k?.orders?.value || 0;
  const totalRevenue = k?.revenue?.value || 0;
  // Held work counts as needing attention: without it, a board with nothing
  // overdue but six stuck orders would say "All caught up".
  const triageTotal = triage
    ? triage.overdue + triage.dueToday + triage.inProduction + triage.ready + (hold?.total || 0)
    : 0;

  // KPI tiles derived from the backend payload.
  const kpiTiles = useMemo(() => {
    if (!k) return [];
    const dr = k.deliveryRate || {};
    return [
      { icon: "▤", iconBg: "rgba(59,130,246,.12)", iconColor: "#60a5fa", label: "Total orders", value: totalOrders.toLocaleString("en-US"), delta: fmtDelta(k.orders.delta), deltaColor: deltaColor(k.orders.delta, c.muted), sub: (k.teams?.active || 0) + " of " + (k.teams?.total || 0) + " teams active" },
      { icon: "৳", iconBg: "rgba(16,185,129,.12)", iconColor: "#34d399", label: "Total revenue", value: fmtShort(totalRevenue), delta: fmtDelta(k.revenue.delta), deltaColor: deltaColor(k.revenue.delta, c.muted), sub: fmtBDT(totalRevenue) },
      { icon: "✓", iconBg: "rgba(139,92,246,.12)", iconColor: "#a78bfa", label: "Delivery rate", ring: true, ringValue: dr.rate || 0, value: (dr.rate ?? 0).toFixed(1) + "%", delta: fmtDelta(dr.delta, "pp"), deltaColor: deltaColor(dr.delta, c.muted), sub: (dr.delivered || 0).toLocaleString("en-US") + " of " + (dr.promised || 0).toLocaleString("en-US") + " promised" },
      { icon: "⇄", iconBg: "rgba(245,158,11,.12)", iconColor: "#fbbf24", label: "Delivered", value: (k.delivered?.value || 0).toLocaleString("en-US"), delta: fmtDelta(k.delivered?.delta), deltaColor: deltaColor(k.delivered?.delta, c.muted), sub: (k.delivered?.returned || 0) + " returned · " + (k.delivered?.cancelled || 0) + " cancelled" },
      k.avgOrderValue
        ? { icon: "≈", iconBg: "rgba(14,165,233,.12)", iconColor: "#38bdf8", label: "Avg order value", value: fmtShort(k.avgOrderValue.value || 0), delta: fmtDelta(k.avgOrderValue.delta), deltaColor: deltaColor(k.avgOrderValue.delta, c.muted), sub: fmtBDT(k.avgOrderValue.value || 0) + " per order" }
        : { icon: "◈", iconBg: "rgba(14,165,233,.12)", iconColor: "#38bdf8", label: "Active teams", value: (k.teams?.active || 0) + " / " + (k.teams?.total || 0), delta: "", deltaColor: c.muted, sub: (k.teams?.members || 0) + " members total" },
      { icon: "◷", iconBg: "rgba(99,102,241,.12)", iconColor: "#818cf8", label: "Avg cycle time", value: k.cycleTime?.days === null || k.cycleTime?.days === undefined ? "—" : k.cycleTime.days + " d", delta: k.cycleTime?.delta === null || k.cycleTime?.delta === undefined ? "" : (k.cycleTime.delta <= 0 ? "▼ " : "▲ ") + Math.abs(k.cycleTime.delta).toFixed(1) + " d", deltaColor: (k.cycleTime?.delta ?? 0) <= 0 ? "#4ade80" : "#fca5a5", sub: "order placed → delivered", spark: k.cycleTime?.spark || [] },
      { icon: "▦", iconBg: "rgba(139,92,246,.12)", iconColor: "#a78bfa", label: "Units sold", value: shortNum(k.unitsSold?.value || 0), delta: fmtDelta(k.unitsSold?.delta), deltaColor: deltaColor(k.unitsSold?.delta, c.muted), sub: (k.unitsSold?.value || 0).toLocaleString("en-US") + " items across all lines" },
      { icon: "↩", iconBg: "rgba(248,113,113,.12)", iconColor: "#fca5a5", label: "Return rate", value: (k.returnRate?.rate ?? 0).toFixed(1) + "%", delta: k.returnRate?.delta === null || k.returnRate?.delta === undefined ? "" : (k.returnRate.delta <= 0 ? "▼ " : "▲ ") + Math.abs(k.returnRate.delta).toFixed(1) + "pp", deltaColor: (k.returnRate?.delta ?? 0) <= 0 ? "#4ade80" : "#fca5a5", sub: (k.returnRate?.returned || 0).toLocaleString("en-US") + " of " + (k.returnRate?.lines || 0).toLocaleString("en-US") + " lines returned" },
    ];
  }, [k, totalOrders, totalRevenue, c.muted]);

  // Triage tiles — current open-work snapshot.
  const triageTiles = useMemo(() => {
    if (!triage) return [];
    return [
      { label: "Overdue", count: triage.overdue, note: "past promised", icon: "!", color: "#f87171", iconBg: "rgba(248,113,113,.14)", bg: "rgba(248,113,113,.05)", border: "rgba(248,113,113,.22)", bucket: "overdue" },
      { label: "Due today", count: triage.dueToday, note: "promised today", icon: "◷", color: "#fbbf24", iconBg: "rgba(245,158,11,.14)", bg: "rgba(245,158,11,.05)", border: "rgba(245,158,11,.22)", bucket: "dueToday" },
      {
        label: "In production",
        count: triage.inProduction,
        note: triage.productionDelayed > 0 ? `${triage.productionDelayed} delayed > 48h` : "with the factory",
        icon: "◍",
        color: triage.productionDelayed > 0 ? "#f87171" : "#fbbf24",
        iconBg: triage.productionDelayed > 0 ? "rgba(248,113,113,.14)" : "rgba(245,158,11,.14)",
        bg: triage.productionDelayed > 0 ? "rgba(248,113,113,.05)" : "rgba(245,158,11,.05)",
        border: triage.productionDelayed > 0 ? "rgba(248,113,113,.22)" : "rgba(245,158,11,.22)",
        bucket: "inProduction",
      },
      { label: "Ready to deliver", count: triage.ready, note: "awaiting pickup", icon: "▲", color: "#34d399", iconBg: "rgba(16,185,129,.14)", bg: "rgba(16,185,129,.05)", border: "rgba(16,185,129,.22)", bucket: "readyToDeliver" },
      { label: "On hold", count: hold?.total || 0, note: "not moving", icon: HOLD_STYLE.icon, color: HOLD_STYLE.color, iconBg: HOLD_STYLE.bg, bg: "rgba(232,121,249,.05)", border: "rgba(232,121,249,.22)", bucket: "onHold" },
    ];
  }, [triage, hold]);

  // Volume + revenue chart geometry.
  const chart = useMemo(() => {
    const series = data?.volume || [];
    const left = 42, right = 432, top = 24, base = 210;
    const n = Math.max(1, series.length);
    const maxO = Math.max(1, ...series.map((d) => d.orders));
    const maxR = Math.max(1, ...series.map((d) => d.revenue));
    const step = (right - left) / n;
    const bw = Math.max(3, Math.min(26, step * 0.58));
    const bars = series.map((d, i) => {
      const h = (d.orders / maxO) * (base - top);
      return { x: (left + step * i + (step - bw) / 2).toFixed(1), y: (base - h).toFixed(1), w: bw.toFixed(1), h: Math.max(2, h).toFixed(1) };
    });
    const revenueLine = series
      .map((d, i) => `${(left + step * i + step / 2).toFixed(1)},${(base - (d.revenue / maxR) * (base - top)).toFixed(1)}`)
      .join(" ");
    const ticks = [0, 1, 2, 3, 4];
    const vy = (y) => ((y / 250) * 100).toFixed(2) + "%";
    const vx = (x) => ((x / 480) * 100).toFixed(2) + "%";
    const gridLines = ticks.map((t) => ({ y: (base - ((base - top) * t) / 4).toFixed(1) }));
    const axisLeft = ticks.map((t) => ({ top: vy(base - ((base - top) * t) / 4), label: shortNum((maxO * t) / 4) }));
    const axisRight = ticks.map((t) => ({ top: vy(base - ((base - top) * t) / 4), label: shortNum((maxR * t) / 4) }));
    const cand = series.map((d, i) => ({ left: vx(left + step * i + step / 2), label: d.label }));
    const everyN = Math.max(1, Math.ceil(cand.length / 8));
    const xLabels = cand.filter((_, i) => i % everyN === 0);
    return { bars, revenueLine, gridLines, axisLeft, axisRight, xLabels };
  }, [data]);

  // Channel breakdown split + rows. The API keys are still the pre-rename
  // marketplace/merchant ones; the channels they carry are Online and Offline.
  const channel = useMemo(() => {
    if (!breakdown) return null;
    const mkt = breakdown.marketplaceOrders || 0;
    const mch = breakdown.merchantOrders || 0;
    const tot = Math.max(1, mkt + mch);
    const sources = breakdown.sources || [];
    const maxSrc = Math.max(1, ...sources.map((s) => s.orders));
    return {
      mkt, mch, tot,
      splitMkt: ((mkt / tot) * 100).toFixed(1) + "%",
      splitMch: ((mch / tot) * 100).toFixed(1) + "%",
      rows: sources.map((s, i) => ({ ...s, color: SOURCE_COLORS[i % SOURCE_COLORS.length], width: ((s.orders / maxSrc) * 100).toFixed(1) + "%" })),
    };
  }, [breakdown]);

  // 12-month trend line geometry.
  const trendChart = useMemo(() => {
    if (!trend || !trend.lines || !trend.lines.length) return null;
    const cl = 40, cr = 444, ct = 20, cb = 225;
    const maxT = Math.max(1, ...trend.lines.flatMap((l) => l.points));
    const lines = trend.lines.map((l, i) => ({
      name: l.name,
      color: TREND_COLORS[i % TREND_COLORS.length],
      points: l.points.map((v, j) => `${(cl + ((cr - cl) * j) / Math.max(1, l.points.length - 1)).toFixed(1)},${(cb - (v / maxT) * (cb - ct)).toFixed(1)}`).join(" "),
    }));
    const ticks = [0, 1, 2, 3, 4];
    const grid = ticks.map((t) => ({ y: (cb - ((cb - ct) * t) / 4).toFixed(1) }));
    const axis = ticks.map((t) => ({ top: (((cb - ((cb - ct) * t) / 4) / 260) * 100).toFixed(2) + "%", label: shortNum((maxT * t) / 4) }));
    const months = (trend.months || []).map((m, i) => ({ left: (((cl + ((cr - cl) * i) / Math.max(1, trend.months.length - 1)) / 460) * 100).toFixed(2) + "%", label: m }));
    return { lines, grid, axis, months };
  }, [trend]);

  // Product breakdown for the selected dimension + metric (rows carry both deltas,
  // so switching Revenue⇄Units and Fabric/Type/Color needs no refetch).
  const productBreak = useMemo(() => {
    const dim = data?.products?.breakdown?.[productDim];
    if (!dim || !dim.rows || !dim.rows.length) return null;
    const byRevenue = productMetric === "revenue";
    const max = Math.max(1, ...dim.rows.map((r) => (byRevenue ? r.revenue : r.units)));
    return {
      rows: dim.rows.map((r, i) => ({
        name: r.name,
        value: byRevenue ? fmtShort(r.revenue) : r.units.toLocaleString("en-US") + " u",
        sub: byRevenue ? r.units.toLocaleString("en-US") + " units" : fmtShort(r.revenue),
        delta: byRevenue ? r.revenueDelta : r.unitsDelta,
        width: (((byRevenue ? r.revenue : r.units) / max) * 100).toFixed(1) + "%",
        color: SOURCE_COLORS[i % SOURCE_COLORS.length],
      })),
    };
  }, [data, productDim, productMetric]);

  const subtitle = loading
    ? "Loading…"
    : totalOrders === 0
    ? `No orders recorded · ${board}`
    : `${totalOrders.toLocaleString("en-US")} orders · ${fmtBDT(totalRevenue)} · ${board}`;

  const cardBase = { background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14 };
  const rankStyles = [
    { rankBg: "rgba(217,164,65,.14)", rankBorder: "rgba(217,164,65,.35)", rankColor: "#e3b45f" },
    { rankBg: "rgba(154,166,184,.12)", rankBorder: "rgba(154,166,184,.3)", rankColor: "#b6c0cf" },
    { rankBg: "rgba(176,125,79,.14)", rankBorder: "rgba(176,125,79,.32)", rankColor: "#c99268" },
  ];

  const skeleton = (w, h = 20) => <div style={{ height: h, width: w, borderRadius: 6, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />;

  return (
    <div className="ov-root" style={{ background: c.bg, color: c.text, fontFamily: SANS, borderRadius: 14, padding: "22px 22px 30px", display: "flex", flexDirection: "column", gap: 18 }}>
      <style>{`
        @keyframes ovPulse{0%,100%{opacity:.35}50%{opacity:.7}}
        @keyframes ovFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
        .ov-hover:hover{background:${c.surface2} !important}
        .ov-grid{display:grid;grid-template-columns:1.7fr 1fr;gap:18px;align-items:start}
        .ov-col{display:flex;flex-direction:column;gap:18px;min-width:0}
        @media (max-width:1000px){
          .ov-grid{grid-template-columns:1fr !important}
          .ov-kpis{grid-template-columns:repeat(2,1fr) !important}
        }
        @media (max-width:520px){
          .ov-kpis{grid-template-columns:1fr !important}
        }
        @media (max-width:640px){
          .ov-root{padding:14px 12px 22px !important;border-radius:10px !important}
          .ov-ranges{flex-wrap:wrap !important}
        }
      `}</style>

      {/* Title + range selector */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.text }}>Overview</div>
          <div style={{ fontSize: 13, color: c.muted, marginTop: 3 }}>{subtitle}</div>
        </div>
        <div className="ov-ranges" style={{ display: "flex", background: c.surface, border: `1px solid ${c.border}`, borderRadius: 9, padding: 3, gap: 2 }}>
          {RANGES.map((r) => (
            <div key={r.key} onClick={() => setRange(r.key)} style={{ padding: "0 14px", height: 30, display: "flex", alignItems: "center", borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: "pointer", ...seg(range === r.key) }}>
              {r.label}
            </div>
          ))}
        </div>
      </div>

      {/* Renders nothing until courier credentials are configured. Fetches on
          its own so a slow courier cannot hold up the board. */}
      <CourierBalanceCard palette={c} cardBase={cardBase} mono={MONO} />

      {/* KPI grid — 8 headline metrics, 4 × 2 on wide screens */}
      <div className="ov-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 11 }}>
        {(loading ? [0, 1, 2, 3, 4, 5, 6, 7].map(() => null) : kpiTiles).map((tile, i) => {
          const C = 2 * Math.PI * 17;
          const ringColor = tile && tile.ringValue >= 92 ? "#34d399" : tile && tile.ringValue >= 85 ? "#fbbf24" : "#f87171";
          const spark = tile?.spark || [];
          const mx = spark.length ? Math.max(...spark) : 1;
          const mn = spark.length ? Math.min(...spark) : 0;
          const sparkPts = spark.map((v, j) => `${((j / Math.max(1, spark.length - 1)) * 60 + 1).toFixed(1)},${(2 + (1 - (v - mn) / Math.max(0.001, mx - mn)) * 20).toFixed(1)}`).join(" ");
          return (
            <div key={i} style={{ ...cardBase, borderRadius: 11, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 7, minWidth: 0 }}>
              {tile ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, background: tile.iconBg, color: tile.iconColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>{tile.icon}</span>
                    <span style={{ fontSize: 11.5, color: c.muted, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tile.label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, minHeight: 30 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap", minWidth: 0 }}>
                      <div style={{ fontSize: 21, fontWeight: 700, color: c.text, fontFamily: MONO, lineHeight: 1 }}>{tile.value}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: tile.deltaColor }}>{tile.delta}</div>
                    </div>
                    {tile.ring && (
                      <div style={{ position: "relative", width: 30, height: 30, flexShrink: 0, marginLeft: "auto" }}>
                        <svg width="30" height="30" viewBox="0 0 42 42">
                          <circle cx="21" cy="21" r="17" fill="none" stroke={c.surface2} strokeWidth="4" />
                          <circle cx="21" cy="21" r="17" fill="none" stroke={ringColor} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${((C * tile.ringValue) / 100).toFixed(1)} ${C}`} transform="rotate(-90 21 21)" />
                        </svg>
                      </div>
                    )}
                    {spark.length > 1 && (
                      <svg width="56" height="20" viewBox="0 0 62 24" style={{ flexShrink: 0, marginLeft: "auto" }}>
                        <polyline points={sparkPts} fill="none" stroke="#4ade80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div style={{ fontSize: 10.5, color: c.dim, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tile.sub}</div>
                </>
              ) : (
                <>
                  {skeleton("48%", 12)}
                  {skeleton("68%", 20)}
                  {skeleton("56%", 10)}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Needs attention */}
      <div style={{ ...cardBase, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 13 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: triageTotal === 0 ? "#34d399" : triage?.overdue > 0 ? "#f87171" : "#fbbf24" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Needs attention</span>
            <span style={{ fontSize: 11.5, color: c.dim }}>{loading ? "" : triageTotal === 0 ? "nothing open" : triageTotal.toLocaleString("en-US") + " open · " + (triage?.overdue || 0) + " overdue"}</span>
          </div>
          <div onClick={() => navigate("/orders", { state: { bucket: "all" } })} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>All orders →</div>
        </div>
        {loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>{[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 74, borderRadius: 11, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />)}</div>
        ) : triageTotal === 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "16px 4px" }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, background: "rgba(16,185,129,.12)", color: "#34d399", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>✓</span>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>All caught up</div>
              <div style={{ fontSize: 12.5, color: c.muted, marginTop: 2 }}>Nothing overdue, nothing waiting on production right now.</div>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
            {triageTiles.map((b, i) => (
              <div key={i} onClick={() => navigate("/orders", { state: { bucket: b.bucket } })} style={{ border: `1px solid ${b.border}`, background: b.bg, borderRadius: 11, padding: "12px 13px", display: "flex", flexDirection: "column", gap: 9, cursor: "pointer", minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 7, background: b.iconBg, color: b.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>{b.icon}</span>
                  <span style={{ fontSize: 10.5, color: c.dim }}>↗</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: b.color, fontFamily: MONO, lineHeight: 1 }}>{b.count.toLocaleString("en-US")}</span>
                  <span style={{ fontSize: 11, color: c.dim }}>{b.note}</span>
                </div>
                <div style={{ fontSize: 11.5, color: c.muted, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* What the held work is waiting on — the tile says how many stopped,
            this says why, which is the question the tile can't answer. */}
        {!loading && hold?.total > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", fontSize: 11.5, color: c.muted }}>
            <span style={{ color: c.dim }}>Held for</span>
            {HOLD_BREAKDOWN.map(([key, label]) => (
              hold.byReason?.[key] ? (
                <span key={key}>
                  <span style={{ fontFamily: MONO, fontWeight: 700, color: c.text }}>{hold.byReason[key]}</span> {label}
                </span>
              ) : null
            ))}
            {hold.tooLong > 0 && (
              <span style={{ color: "#f87171", fontWeight: 600 }}>
                · {hold.tooLong} over {hold.thresholdDays} days
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main column (charts) + right rail (lists) */}
      <div className="ov-grid">
        {/* Left / main column */}
        <div className="ov-col">

        {/* Volume & revenue */}
      <div style={{ ...cardBase, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Volume &amp; revenue</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{loading ? "" : totalOrders === 0 ? "No data for " + board : "Orders and revenue · " + board}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: accentColor }} /><span style={{ fontSize: 11.5, color: c.muted }}>Orders</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}><span style={{ width: 14, height: 2, borderRadius: 2, background: "#34d399" }} /><span style={{ fontSize: 11.5, color: c.muted }}>Revenue</span></div>
          </div>
        </div>
        {loading ? (
          <div style={{ height: 268, margin: 18, borderRadius: 10, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />
        ) : totalOrders === 0 ? (
          <div style={{ height: 268, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, textAlign: "center" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: c.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: c.dim }}>▤</div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>No orders in this range</div>
            <div style={{ fontSize: 12.5, color: c.muted, maxWidth: 340 }}>Nothing was placed in {board}. Widen the range to see trend data.</div>
          </div>
        ) : (
          <div style={{ padding: "18px 18px 12px" }}>
            <div style={{ position: "relative", width: "100%" }}>
              <svg viewBox="0 0 480 250" style={{ width: "100%", height: "auto", display: "block" }}>
                {chart.gridLines.map((g, i) => <line key={i} x1="42" y1={g.y} x2="432" y2={g.y} stroke={darkMode ? "#1c222d" : "#eef2f7"} strokeWidth="1" />)}
                {chart.bars.map((b, i) => <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} rx="3" fill={accentColor} />)}
                <polyline points={chart.revenueLine} fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {chart.axisLeft.map((a, i) => <div key={"l" + i} style={{ position: "absolute", left: 0, width: "7.5%", top: a.top, transform: "translateY(-50%)", textAlign: "right", fontSize: 10, color: c.dim, fontFamily: MONO }}>{a.label}</div>)}
              {chart.axisRight.map((a, i) => <div key={"r" + i} style={{ position: "absolute", left: "91.5%", top: a.top, transform: "translateY(-50%)", fontSize: 10, color: c.dim, fontFamily: MONO }}>{a.label}</div>)}
              {chart.xLabels.map((x, i) => <div key={"x" + i} style={{ position: "absolute", left: x.left, top: "91%", transform: "translateX(-50%)", fontSize: 10, color: c.dim, fontFamily: MONO, whiteSpace: "nowrap" }}>{x.label}</div>)}
            </div>
          </div>
        )}
      </div>

      {/* Channel performance */}
      <div style={{ ...cardBase, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Channel performance</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{channelMode === "breakdown" ? "Order sources · " + board : "Top 5 sources · last 12 months"}</div>
          </div>
          <div style={{ display: "flex", background: c.surface2, border: `1px solid ${c.border}`, borderRadius: 8, padding: 3, gap: 2 }}>
            {CHANNEL_MODES.map(([key, label]) => (
              <div key={key} onClick={() => setChannelMode(key)} style={{ padding: "0 12px", height: 28, display: "flex", alignItems: "center", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", ...seg(channelMode === key) }}>{label}</div>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ height: 38, borderRadius: 8, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />
            {["86%", "74%", "80%", "66%", "71%"].map((w, i) => <div key={i} style={{ height: 26, width: w, borderRadius: 6, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />)}
          </div>
        ) : channelMode === "breakdown" ? (
          !channel || (channel.mkt === 0 && channel.mch === 0) ? (
            <div style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: c.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: c.dim }}>◈</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>No channel activity</div>
              <div style={{ fontSize: 12.5, color: c.muted }}>No online or offline orders in this range.</div>
            </div>
          ) : (
            <div style={{ padding: 18 }}>
              <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", gap: 2, marginBottom: 12 }}>
                <div style={{ width: channel.splitMkt, background: "#3b82f6" }} />
                <div style={{ width: channel.splitMch, background: "#8b5cf6" }} />
              </div>
              <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: "#3b82f6" }} />
                  <span style={{ fontSize: 12.5, color: c.text }}>Online</span>
                  <span style={{ fontSize: 12.5, color: c.muted, fontFamily: MONO }}>{channel.mkt.toLocaleString("en-US")} · {Math.round((channel.mkt / channel.tot) * 100)}%</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: "#8b5cf6" }} />
                  <span style={{ fontSize: 12.5, color: c.text }}>Offline</span>
                  <span style={{ fontSize: 12.5, color: c.muted, fontFamily: MONO }}>{channel.mch.toLocaleString("en-US")} · {Math.round((channel.mch / channel.tot) * 100)}%</span>
                </div>
              </div>
              {channel.rows.map((s, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: 7, padding: "11px 2px", borderBottom: `1px solid ${c.border}`, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
                    <span style={{ fontSize: 12.5, color: c.text, fontFamily: MONO, flexShrink: 0 }}>{s.orders.toLocaleString("en-US")}</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: c.surface2 }}>
                    <div style={{ height: 4, borderRadius: 2, width: s.width, background: s.color }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0, paddingLeft: 16 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: c.muted, fontFamily: MONO, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtShort(s.revenue)}{s.type && s.type !== s.name ? ` · ${s.type}` : ""}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor(s.delta, c.muted), flexShrink: 0 }}>{fmtDelta(s.delta)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : !trendChart ? (
          <div style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: c.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: c.dim }}>◈</div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>No trend data</div>
            <div style={{ fontSize: 12.5, color: c.muted }}>No orders recorded in the last 12 months.</div>
          </div>
        ) : (
          <div style={{ padding: 18 }}>
            <div style={{ position: "relative", width: "100%" }}>
              <svg viewBox="0 0 460 260" style={{ width: "100%", height: "auto", display: "block" }}>
                {trendChart.grid.map((g, i) => <line key={i} x1="40" y1={g.y} x2="450" y2={g.y} stroke={darkMode ? "#1c222d" : "#eef2f7"} strokeWidth="1" />)}
                {trendChart.lines.map((l, i) => <polyline key={i} points={l.points} fill="none" stroke={l.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />)}
              </svg>
              {trendChart.axis.map((a, i) => <div key={"a" + i} style={{ position: "absolute", left: 0, width: "7.2%", top: a.top, transform: "translateY(-50%)", textAlign: "right", fontSize: 10, color: c.dim, fontFamily: MONO }}>{a.label}</div>)}
              {trendChart.months.map((m, i) => <div key={"m" + i} style={{ position: "absolute", left: m.left, top: "93%", transform: "translateX(-50%)", fontSize: 10, color: c.dim, fontFamily: MONO, whiteSpace: "nowrap" }}>{m.label}</div>)}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, paddingLeft: 40 }}>
              {trendChart.lines.map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ width: 14, height: 2, borderRadius: 2, background: l.color }} />
                  <span style={{ fontSize: 11.5, color: c.muted }}>{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Product performance */}
      <div style={{ ...cardBase, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Product performance</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{loading ? "" : (PRODUCT_DIMS.find((d) => d[0] === productDim)?.[1] || "") + " · by " + (productMetric === "revenue" ? "revenue" : "units") + " · " + board}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", background: c.surface2, border: `1px solid ${c.border}`, borderRadius: 8, padding: 3, gap: 2 }}>
              {PRODUCT_DIMS.map(([key, label]) => (
                <div key={key} onClick={() => setProductDim(key)} style={{ padding: "0 11px", height: 28, display: "flex", alignItems: "center", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", ...seg(productDim === key) }}>{label}</div>
              ))}
            </div>
            <div style={{ display: "flex", background: c.surface2, border: `1px solid ${c.border}`, borderRadius: 8, padding: 3, gap: 2 }}>
              {PRODUCT_METRICS.map(([key, label]) => (
                <div key={key} onClick={() => setProductMetric(key)} style={{ padding: "0 11px", height: 28, display: "flex", alignItems: "center", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", ...seg(productMetric === key) }}>{label}</div>
              ))}
            </div>
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
            {["86%", "74%", "80%", "66%", "71%"].map((w, i) => <div key={i} style={{ height: 26, width: w, borderRadius: 6, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />)}
          </div>
        ) : !productBreak ? (
          <div style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: c.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: c.dim }}>▦</div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>No product data</div>
            <div style={{ fontSize: 12.5, color: c.muted }}>No line items were recorded in this range.</div>
          </div>
        ) : (
          <div style={{ padding: 18 }}>
            {productBreak.rows.map((r, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 7, padding: "11px 2px", borderBottom: `1px solid ${c.border}`, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                  <span style={{ fontSize: 12.5, color: c.text, fontFamily: MONO, flexShrink: 0 }}>{r.value}</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: c.surface2 }}>
                  <div style={{ height: 4, borderRadius: 2, width: r.width, background: r.color }} />
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0, paddingLeft: 16 }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: c.muted, fontFamily: MONO, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.sub}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor(r.delta, c.muted), flexShrink: 0 }}>{fmtDelta(r.delta)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

        {/* Stuck on hold — the orders behind the On hold tile at the top, as a
            list you scroll rather than a page you paginate. Last in the left
            column: it is a working list to come back to, not something the
            charts should have to scroll past. */}
        {!loading && hold?.tooLong > 0 && (
        <div style={{ ...cardBase, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: HOLD_STYLE.color }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Stuck on hold</span>
              <span style={{ fontSize: 11.5, color: c.dim }}>{hold.tooLong} held over {hold.thresholdDays} days</span>
            </div>
            <div onClick={() => navigate("/orders", { state: { bucket: "onHold", heldOverDays: hold.thresholdDays } })} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>View in orders →</div>
          </div>

          {/* Scrolls at roughly six rows; the rest is a scroll, not a click. */}
          <div style={{ maxHeight: 380, overflowY: "auto", margin: "0 -4px" }}>
            {(hold.stuck || []).map((h, i) => (
              <div
                key={h.id}
                onClick={() => navigate(`/orders/${h.id}`)}
                className="ov-hover"
                style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "baseline", padding: "9px 6px", borderTop: i === 0 ? "none" : `1px solid ${c.border}`, cursor: "pointer", minWidth: 0 }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: c.text, fontFamily: MONO, flexShrink: 0 }}>#{h.id}</span>
                    <span style={{ fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.orderedBy}</span>
                    {holdReasonLabel(h.reason) && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: HOLD_STYLE.color, background: HOLD_STYLE.bg, borderRadius: 6, padding: "1px 6px", flexShrink: 0 }}>
                        {holdReasonLabel(h.reason)}
                      </span>
                    )}
                    {h.fromStatus != null && (
                      <span style={{ fontSize: 10.5, color: c.dim, whiteSpace: "nowrap", flexShrink: 0 }}>
                        from {statusMeta(h.fromStatus).label}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: h.comment ? c.muted : c.dim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {h.comment ? `“${h.comment}”` : "No reason recorded"}
                  </div>
                  <div style={{ fontSize: 10.5, color: c.dim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {[h.by && `held by ${h.by}`, h.since && fmtDateTime(h.since), h.team].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "#fca5a5", background: "rgba(248,113,113,.12)", borderRadius: 6, padding: "3px 8px", fontFamily: MONO, flexShrink: 0 }}>{h.days}d</span>
              </div>
            ))}
          </div>

          {/* Only when the server's cap actually bit — normally the list is whole. */}
          {hold.tooLong > (hold.stuck || []).length && (
            <div onClick={() => navigate("/orders", { state: { bucket: "onHold", heldOverDays: hold.thresholdDays } })} style={{ fontSize: 11.5, color: c.muted, cursor: "pointer" }}>
              +{hold.tooLong - (hold.stuck || []).length} more not shown →
            </div>
          )}
        </div>
        )}

      </div>

        {/* Right / rail: delayed in production → dormant → ready stock → top performers → recent activity */}
        <div className="ov-col">
        {/* Delayed in production — orders in PRINT or PRODUCTION for over 48h */}
        {!loading && delayedProduction?.total > 0 && (
        <div style={{ ...cardBase, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#f87171" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Delayed in production</span>
              <span style={{ fontSize: 11.5, color: c.dim }}>
                {delayedProduction.total} delayed over {delayedProduction.thresholdHours}h
                {delayedProduction.byStatus && (
                  <span style={{ marginLeft: 6, color: c.muted }}>
                    ({[
                      delayedProduction.byStatus.print != null && `${delayedProduction.byStatus.print} print`,
                      (delayedProduction.byStatus.cutting ?? delayedProduction.byStatus.production) != null && `${delayedProduction.byStatus.cutting ?? delayedProduction.byStatus.production} cutting`,
                      delayedProduction.byStatus.sewing != null && `${delayedProduction.byStatus.sewing} sewing`,
                    ].filter(Boolean).join(" · ")})
                  </span>
                )}
              </span>
            </div>
            <div
              onClick={() => navigate("/orders", { state: { bucket: "inProduction", delayedOverHours: delayedProduction.thresholdHours } })}
              style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}
            >
              View in orders →
            </div>
          </div>

          {/* Scrolls at roughly six rows */}
          <div style={{ maxHeight: 380, overflowY: "auto", margin: "0 -4px" }}>
            {(delayedProduction.orders || []).map((o, i) => {
              const sm = statusMeta(o.status, darkMode);
              return (
                <div
                  key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  className="ov-hover"
                  style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "baseline", padding: "9px 6px", borderTop: i === 0 ? "none" : `1px solid ${c.border}`, cursor: "pointer", minWidth: 0 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: c.text, fontFamily: MONO, flexShrink: 0 }}>#{o.id}</span>
                      <span style={{ fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.orderedBy}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: sm.solid || sm.color, background: sm.bg || "rgba(239,68,68,.12)", borderRadius: 6, padding: "1px 6px", flexShrink: 0 }}>
                        {sm.label}
                      </span>
                      {o.team && (
                        <span style={{ fontSize: 10.5, color: c.dim, whiteSpace: "nowrap", flexShrink: 0 }}>
                          · {o.team}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10.5, color: c.dim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {o.since ? `in production since ${fmtDateTime(o.since)}` : ""}
                    </div>
                  </div>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "#fca5a5", background: "rgba(248,113,113,.12)", borderRadius: 6, padding: "3px 8px", fontFamily: MONO, flexShrink: 0 }}>
                    {o.hours}h ({o.days}d)
                  </span>
                </div>
              );
            })}
          </div>

          {/* Overflow count link */}
          {delayedProduction.total > (delayedProduction.orders || []).length && (
            <div
              onClick={() => navigate("/orders", { state: { bucket: "inProduction", delayedOverHours: delayedProduction.thresholdHours } })}
              style={{ fontSize: 11.5, color: c.muted, cursor: "pointer" }}
            >
              +{delayedProduction.total - (delayedProduction.orders || []).length} more not shown →
            </div>
          )}
        </div>
        )}

        {/* Dormant merchants (company scope only) */}
        {!isPersonal && (
        <div style={{ ...cardBase, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Dormant merchants</span>
            <div onClick={() => navigate("/customers?view=dormant")} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>View →</div>
          </div>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>{[0, 1, 2, 3].map((i) => <div key={i}>{skeleton("100%")}</div>)}</div>
          ) : !dormant || dormant.count === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 0" }}>
              <span style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(16,185,129,.12)", color: "#34d399", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>✓</span>
              <div style={{ fontSize: 12.5, color: c.muted }}>Every regular merchant has ordered in the last 10 days.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: "#fbbf24", fontFamily: MONO, lineHeight: 1 }}>{dormant.count}</span>
                <span style={{ fontSize: 12, color: c.muted }}>merchants need follow-up</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {dormant.items.map((d, i) => (
                  <div key={i} onClick={() => navigate("/customers?view=dormant")} className="ov-hover" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "7px 6px", borderRadius: 7, cursor: "pointer", minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                      <div style={{ fontSize: 10.5, color: c.dim, marginTop: 2 }}>{d.lastOrder ? "last order " + d.lastOrder : "no recent order"}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: d.days === null || d.days >= 60 ? "#fca5a5" : "#fbbf24", background: d.days === null || d.days >= 60 ? "rgba(248,113,113,.12)" : "rgba(245,158,11,.12)", borderRadius: 6, padding: "3px 7px", fontFamily: MONO, flexShrink: 0 }}>{d.days === null ? "—" : d.days + "d"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Ready stock */}
        <div style={{ ...cardBase, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 13, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Ready stock</span>
            <div onClick={() => navigate("/stock")} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>Manage →</div>
          </div>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>{[0, 1, 2].map((i) => <div key={i}>{skeleton("100%", 34)}</div>)}</div>
          ) : !stock || stock.totalSkus === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "8px 0" }}>
              <span style={{ width: 32, height: 32, borderRadius: 9, background: c.surface2, color: c.dim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>▦</span>
              <div style={{ fontSize: 12.5, color: c.muted }}>No ready-stock SKUs are tracked yet.</div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: "#34d399", fontFamily: MONO, lineHeight: 1 }}>{stock.fulfilledPct === null || stock.fulfilledPct === undefined ? "—" : stock.fulfilledPct.toFixed(1) + "%"}</span>
                <span style={{ fontSize: 12, color: c.muted }}>of items shipped from stock</span>
                {stock.fulfilledDelta !== null && stock.fulfilledDelta !== undefined && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: deltaColor(stock.fulfilledDelta, c.muted) }}>{fmtDelta(stock.fulfilledDelta, "pp")}</span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  { label: "On-hand units", value: shortNum(stock.onHandUnits), color: c.text },
                  { label: "SKUs in stock", value: (stock.skusInStock || 0).toLocaleString("en-US") + " / " + (stock.totalSkus || 0).toLocaleString("en-US"), color: c.text },
                  { label: "Low stock", value: (stock.lowStock || 0).toLocaleString("en-US"), color: stock.lowStock > 0 ? "#fbbf24" : c.text },
                  { label: "Out of stock", value: (stock.zeroStock || 0).toLocaleString("en-US"), color: stock.zeroStock > 0 ? "#fca5a5" : c.text },
                ].map((s, i) => (
                  <div key={i} style={{ border: `1px solid ${c.border}`, background: c.surface2, borderRadius: 9, padding: "9px 11px", minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: s.color, fontFamily: MONO, lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 10.5, color: c.dim, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {stock.topStocked && stock.topStocked.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: c.dim, margin: "2px 0 3px" }}>Most stocked</div>
                  {stock.topStocked.map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "6px 4px", minWidth: 0 }}>
                      <span style={{ minWidth: 0, fontSize: 12, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: c.muted, fontFamily: MONO, flexShrink: 0 }}>{(s.onHand || 0).toLocaleString("en-US")}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Top performers */}
        <div style={{ ...cardBase, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Top performers</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{isPersonal ? "My team" : "Across all teams"} · {board}</div>
          </div>
          {loading ? (
            <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 16 }}>{["86%", "74%", "80%", "66%", "71%"].map((w, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>{skeleton(w, 12)}{skeleton("80px", 10)}</div>
                {skeleton("70px", 12)}
              </div>
            ))}</div>
          ) : performers.length === 0 ? (
            <div style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: c.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: c.dim }}>☰</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>No member activity</div>
              <div style={{ fontSize: 12.5, color: c.muted }}>No orders created by any member in this range.</div>
            </div>
          ) : (
            <div style={{ padding: "8px 10px 12px" }}>
              {performers.map((p, i) => {
                const rk = rankStyles[i] || { rankBg: c.surface2, rankBorder: c.border, rankColor: c.muted };
                return (
                  <div key={p.id} onClick={() => navigate("/dashboard/teams")} className="ov-hover" style={{ display: "grid", gridTemplateColumns: "26px 1fr auto", alignItems: "center", gap: 11, padding: "9px 8px", borderRadius: 9, cursor: "pointer" }}>
                    <span style={{ width: 26, height: 26, borderRadius: 8, background: rk.rankBg, border: `1px solid ${rk.rankBorder}`, color: rk.rankColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, fontFamily: MONO }}>{i + 1}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                      <span style={{ width: 28, height: 28, borderRadius: "50%", background: avatarBg(p.name), color: "#fff", fontSize: 10.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials(p.name)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: c.text, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                          <span style={{ fontSize: 10.5, color: c.dim, background: c.surface2, borderRadius: 5, padding: "1px 6px", whiteSpace: "nowrap" }}>{p.team}</span>
                          <span style={{ fontSize: 10.5, color: c.dim }}>{p.orders} orders</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12.5, color: c.text, fontWeight: 600, fontFamily: MONO }}>{fmtBDT(p.revenue)}</div>
                      <div style={{ fontSize: 10.5, color: deltaColor(p.delta, c.dim), fontWeight: 700, marginTop: 2 }}>{fmtDelta(p.delta)}</div>
                    </div>
                  </div>
                );
              })}
              <div onClick={() => navigate("/dashboard/teams")} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 34, margin: "8px 8px 0", borderRadius: 8, border: `1px solid ${c.border}`, background: c.surface2, color: c.text, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>All members by team →</div>
            </div>
          )}
        </div>

        <div style={{ ...cardBase, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Recent activity</div>
              <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>Status changes across all teams</div>
            </div>
            <div onClick={() => navigate("/orders", { state: { bucket: "all" } })} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>All orders →</div>
          </div>
          {loading ? (
            <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 16 }}>{["86%", "74%", "80%", "66%", "71%"].map((w, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.surface2, animation: "ovPulse 1.3s ease-in-out infinite" }} />
                <div style={{ flex: 1 }}>{skeleton(w, 12)}</div>
                {skeleton("44px", 10)}
              </div>
            ))}</div>
          ) : activity.length === 0 ? (
            <div style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: c.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, color: c.dim }}>◷</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>No recent activity</div>
              <div style={{ fontSize: 12.5, color: c.muted }}>No order status has changed recently.</div>
            </div>
          ) : (
            <div style={{ maxHeight: 392, overflowY: "auto", padding: "6px 10px 10px" }}>
              {activity.map((a, i) => {
                const st = statusStyle(a.statusName);
                return (
                  <div key={i} onClick={() => navigate(`/orders/${a.orderId}`)} className="ov-hover" style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", gap: 11, padding: "10px 8px", borderRadius: 9, cursor: "pointer" }}>
                    <span style={{ width: 26, height: 26, borderRadius: 8, background: st.bg, color: st.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, flexShrink: 0 }}>{st.icon}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: c.text, lineHeight: 1.35 }}>
                        <span style={{ fontFamily: MONO, color: accentColor, fontWeight: 600 }}>#{a.orderId}</span>
                        <span style={{ color: c.dim }}> → </span>
                        <span style={{ color: st.color, fontWeight: 600 }}>{a.statusName}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 4, minWidth: 0 }}>
                        <span style={{ width: 19, height: 19, borderRadius: "50%", background: avatarBg(a.by), color: "#fff", fontSize: 8.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials(a.by)}</span>
                        <span style={{ minWidth: 0, fontSize: 10.5, color: c.dim, lineHeight: 1.35, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>by {a.by} · {a.team}</span>
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: c.dim, fontFamily: MONO, flexShrink: 0 }}>{timeAgo(a.at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
